import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import CreateAccount from './CreateAccount';
import ConfirmGym from './ConfirmGym';
import SetHours from './SetHours';
import PaymentSetup from './PaymentSetup';
import OnboardingSuccess from './OnboardingSuccess';
import WhatsNext from './WhatsNext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STEPS = {
  LOADING: 0,
  CREATE_ACCOUNT: 1,
  CONFIRM_GYM: 2,
  SET_HOURS: 3,
  PAYMENT: 4,
  SUCCESS: 5,
  WHATS_NEXT: 6
};

const ManagerOnboarding = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(STEPS.LOADING);
  const [gymData, setGymData] = useState(null);
  const [error, setError] = useState(null);
  const [onboardingData, setOnboardingData] = useState({
    password: '',
    phone: null,
    managerName: null,
    hours: null,
    stripePaymentMethodId: null,
    boardStatuses: {},
    editedGymData: null
  });

  // Validate token on load
  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/onboard/${token}`);

      if (response.data.valid) {
        setGymData(response.data);
        setCurrentStep(STEPS.CREATE_ACCOUNT);
      } else {
        setError('Invalid or expired link');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to validate link');
    }
  };

  const handleStepComplete = (stepData) => {
    setOnboardingData(prev => ({ ...prev, ...stepData }));

    switch (currentStep) {
      case STEPS.CREATE_ACCOUNT:
        setCurrentStep(STEPS.CONFIRM_GYM);
        break;
      case STEPS.CONFIRM_GYM:
        setCurrentStep(STEPS.SET_HOURS);
        break;
      case STEPS.SET_HOURS:
        // Skip payment step for free plans
        if (gymData?.subscription?.plan_type === 'free') {
          completeOnboarding({ stripePaymentMethodId: null });
        } else {
          setCurrentStep(STEPS.PAYMENT);
        }
        break;
      case STEPS.PAYMENT:
        completeOnboarding(stepData);
        break;
      default:
        break;
    }
  };

  const completeOnboarding = async (paymentData) => {
    try {
      const response = await axios.post(`${API_URL}/api/onboard/${token}/complete`, {
        password: onboardingData.password,
        phone: onboardingData.phone,
        managerName: onboardingData.managerName,
        hours: onboardingData.hours,
        stripePaymentMethodId: paymentData.stripePaymentMethodId,
        boardStatuses: onboardingData.boardStatuses,
        editedGymData: onboardingData.editedGymData
      });

      if (response.data.success) {
        // Store auth token
        localStorage.setItem('token', response.data.authToken);
        localStorage.setItem('user', JSON.stringify({
          id: response.data.userId,
          isGymManager: true
        }));

        setCurrentStep(STEPS.SUCCESS);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to complete onboarding';
      const errorDetails = err.response?.data?.details;
      const errorCode = err.response?.data?.code;
      const errorId = err.response?.data?.errorId;

      // Store structured error for bug reporting
      setError({
        message: errorMessage,
        details: errorDetails,
        code: errorCode,
        errorId: errorId,
        timestamp: new Date().toISOString(),
        gymName: gymData?.gym?.name,
        page: 'Manager Onboarding - Payment Step'
      });
    }
  };

  const handleBack = () => {
    if (currentStep > STEPS.CREATE_ACCOUNT) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReportBug = async () => {
    try {
      const token = localStorage.getItem('token');
      const errorInfo = typeof error === 'object' ? error : { message: error };

      await axios.post(
        `${API_URL}/api/bug-report`,
        {
          whatHappened: `Onboarding failed with error: ${errorInfo.message}${errorInfo.details ? `\nDetails: ${errorInfo.details}` : ''}${errorInfo.errorId ? `\nError ID: ${errorInfo.errorId}` : ''}${errorInfo.code ? `\nError Code: ${errorInfo.code}` : ''}`,
          whatExpected: 'Onboarding should complete successfully and redirect to dashboard',
          page: errorInfo.page || 'Manager Onboarding',
          userEmail: gymData?.managerEmail || 'Unknown',
          userName: gymData?.managerName || 'Unknown',
          userId: null,
          userRole: 'gym_manager'
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );

      alert('Bug report submitted successfully! We\'ll look into this right away.');
    } catch (err) {
      console.error('Failed to submit bug report:', err);
      alert('Could not submit bug report. Please contact support directly.');
    }
  };

  if (error) {
    const errorMessage = typeof error === 'object' ? error.message : error;
    const showDetails = typeof error === 'object' && (error.details || error.errorId);

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 border-2 border-slate-900 shadow-retro max-w-lg w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Something Went Wrong</h2>
            <p className="text-slate-700 mb-4">{errorMessage}</p>

            {showDetails && (
              <div className="bg-slate-100 border-2 border-slate-300 rounded-lg p-4 text-left text-sm mb-4">
                <p className="font-semibold text-slate-900 mb-2">Technical Details:</p>
                {error.details && <p className="text-slate-700 mb-1">• {error.details}</p>}
                {error.errorId && <p className="text-slate-700 mb-1">• Error ID: {error.errorId}</p>}
                {error.code && <p className="text-slate-700">• Code: {error.code}</p>}
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-lg p-4 border-2 border-slate-900 mb-6">
            <p className="font-bold text-slate-900 mb-2 text-center">We're Here to Help!</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:support@boardrez.com" className="font-semibold text-teal-600 hover:text-teal-700 underline">
                  support@boardrez.com
                </a>
              </div>
              <div className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="tel:+13035551234" className="font-semibold text-teal-600 hover:text-teal-700 underline">
                  (303) 555-1234
                </a>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleReportBug}
              className="px-6 py-3 bg-coral-500 hover:bg-coral-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
            >
              🐛 Report This Issue
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
            >
              ↻ Try Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === STEPS.LOADING) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-semibold text-slate-700">Loading...</p>
        </div>
      </div>
    );
  }

  // Calculate total steps (skip payment step for free plans)
  const isFreeGym = gymData?.subscription?.plan_type === 'free';
  const totalSteps = isFreeGym ? STEPS.SET_HOURS : STEPS.PAYMENT;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 py-8 px-4">
      {/* Progress bar - only show during onboarding steps, not on success or what's next pages */}
      {currentStep !== STEPS.SUCCESS && currentStep !== STEPS.WHATS_NEXT && (
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-slate-600">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm font-semibold text-slate-600">
              {Math.round((currentStep / totalSteps) * 100)}% Complete
            </span>
          </div>
          <div className="h-2 bg-white border-2 border-slate-900 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-purple-500 transition-all duration-500"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Step content */}
      <div className="max-w-2xl mx-auto">
        {currentStep === STEPS.CREATE_ACCOUNT && (
          <CreateAccount
            gymData={gymData}
            onComplete={handleStepComplete}
          />
        )}

        {currentStep === STEPS.CONFIRM_GYM && (
          <ConfirmGym
            gymData={gymData}
            onComplete={handleStepComplete}
            onBack={handleBack}
          />
        )}

        {currentStep === STEPS.SET_HOURS && (
          <SetHours
            gymData={gymData}
            onComplete={handleStepComplete}
            onBack={handleBack}
          />
        )}

        {currentStep === STEPS.PAYMENT && (
          <PaymentSetup
            gymData={gymData}
            onComplete={handleStepComplete}
            onBack={handleBack}
          />
        )}

        {currentStep === STEPS.SUCCESS && (
          <OnboardingSuccess
            gymData={gymData}
            onContinue={() => setCurrentStep(STEPS.WHATS_NEXT)}
          />
        )}

        {currentStep === STEPS.WHATS_NEXT && (
          <WhatsNext gymData={gymData} />
        )}
      </div>
    </div>
  );
};

export default ManagerOnboarding;
